import { useQuery } from "@tanstack/react-query"
import { ExtendedWalkReport, WalkReport } from "../../Interfaces"
import { useContext, useEffect, useState, useCallback } from "react"
import UiContext from "../../context/uiProvider"
import { TABLET, LARGE_TABLET } from "../../utils/screenSize"
import { walkService } from "../../services/walkService"
import Table from "./common/Table"
import { getDisplayWalkTotalTime } from "../../utils/helper"

export default function LocationWalkReportsTable({ locationId }: { locationId: number }) {
  const { deviceView } = useContext(UiContext)
  const [searchTerm, setSearchTerm] = useState<string>("")

  const { data, isLoading } = useQuery<{ walkReports: WalkReport[]; totalCount: number }>(["walkReports", "locationId" + locationId], () => walkService.getSomeReportsByLocation(locationId, 1000, 0), {
    cacheTime: 5000,
    suspense: true,
  })

  const [filteredData, setFilteredData] = useState<ExtendedWalkReport[] | null | undefined>(null)

  useEffect(() => {
    // set viewable users for current user. This can be used later to filter users by organization or location w/out making another api call
    if (data) {
      // flattens some of the nested data for easier access
      const flattenedData: ExtendedWalkReport[] = data.walkReports.map((walkReport: WalkReport) => {
        const totalWalkTime = getDisplayWalkTotalTime(walkReport)

        return {
          ...walkReport,
          createdAt: new Date(walkReport.createdAt).toLocaleString(),
          finishedAt: walkReport.finishedAt ? new Date(walkReport.finishedAt).toLocaleString() : "Not Completed",
          userName: walkReport.user ? `${walkReport.user.firstName || ''} ${walkReport.user.lastName || ''}`.trim() || 'Unknown User' : 'Unknown User',
          walkName: walkReport.walk?.name || 'Unknown Walk',
          isWalkNotes: walkReport.walkNote?.length ? "Yes" : "No",
          isMarkerImages: walkReport.markerImage?.length ? "Yes" : "No",
          totalWalkTime: totalWalkTime,
        }
      })

      // Apply search filter if needed
      if (searchTerm) {
        const filtered = flattenedData.filter(report =>
          report.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          report.walkName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          report.totalWalkTime?.toLowerCase().includes(searchTerm.toLowerCase())
        )
        setFilteredData(filtered)
      } else {
        setFilteredData(flattenedData)
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchTerm])

  const headers = [
    { label: "Completed", key: "finishedAt", to: `walk-report` },
    { label: "Name", key: "userName", to: `walk-report` },
    { label: "Walk", key: "walkName", to: `walk-report` },
  ]
  // Add two more columns on tablets and desktops
  if (deviceView >= TABLET) {
    headers.unshift({ label: "Id", key: "id", to: `walk-report` })
    headers.push({ label: "Total Time", key: "totalWalkTime", to: `walk-report` })
  }
  // Add three additional columns on desktops
  if (deviceView >= LARGE_TABLET) {
    // future implementation
  }

  const handleSearch = useCallback((query: string) => {
    setSearchTerm(query)
  }, [])

  if (!filteredData || isLoading) {
    return null
  }

  return (
    <Table data={filteredData} title={"Walk Reports"} handleSearch={handleSearch} headers={headers} />
  )
}